import * as angular from 'angular';
import * as moment from 'moment';
import * as _ from 'lodash';

import { ILogbookService, IStoreService, IDashboardHelperService, IDateRangeService, IAuthorizationService, IGroupTreeHelperService, ILogbookModalSeries, IExportToCsvService, ILogbookTableService } from '../../services';
import { LogbookEntry, ComponentDefinition, Dashboard, AugmentedEntry, IValueViewModel, Group, LogbookWidgetFilters, Dictionary, DigitalSetSimpleDictionary, TimePeriod, ErrorLevel, GroupDisplayName, TagStringValueDto } from '../../models';
import { Store } from '../../stateManagement/Store';
import { StoreElement } from '../../stateManagement/StoreElement';
import { LogbookTableSeries } from './logbookTable.series';
import { HeadingDefinition } from './HeadingDefinition';
import { LogbookTableConfiguration } from './LogbookTable.configuration';
import { LogbookTableWidget } from './logbookTable.widget';
import { BadgeVM } from '..';
import { getLogbookTableHeadings } from './getLogbookTableHeadings';
import { getUnitName, WideError } from '../../common';
import { guidEmpty, guidGroupValidationPageLink, Roles } from '../../common/constants';
import { toLogbookEntry, toLogbookEntryDto } from '../../common/helpers/logBookEntryConverter';
import { logbookTableWidgetDefinition } from './logbookTable.definition';
import { LogbookTableReadMode } from './LogbookTableReadMode';
import { timeRangeRetroCompatibility } from './timeRangeRetroCompatibility';
import { LogbookWidgetResult } from './LogbookWidgetResult';
import { LogbookSeriesValues } from './LogbookSeriesValues';
import { getTimeRange } from './getTimeRange';

class LogbookTableController implements angular.IComponentController {
    augmentedEntries: AugmentedEntry[];
    commentWidth: string;
    dashboardNames: Dictionary<string>;
    dashboardsDic: Dictionary<Dashboard>;
    digitalSets: DigitalSetSimpleDictionary;
    groups: Group[];
    guidEmpty: string = guidEmpty;
    headings: HeadingDefinition[][];
    isLoading: boolean;
    isLoadingVms: boolean;
    linkIcon: string = 'fa-th-large';
    viewModels: Dictionary<IValueViewModel>;

    readonly boundData: LogbookWidgetResult;
    readonly boundSeries: { viewModel: IValueViewModel; configuration: LogbookTableSeries }[];
    readonly dashboards: Dashboard[];
    readonly isEditMode: boolean;
    readonly item: LogbookTableWidget;
    readonly selectedGroup: Group;
    readonly stringValues: TagStringValueDto[];

    private _entries: LogbookEntry[];
    private _garbages: (() => void)[] = [];
    private _hasManualInputRoles: boolean;

    static $inject = [
        '$state',
        '$translate',
        'storeService',
        'logbookService',
        'dashboardHelperService',
        'dateRangeService',
        'authorizationService',
        'groupTreeHelperService',
        'exportToCsvService',
        'logbookTableService'
    ];

    constructor(
        private _$state: angular.ui.IStateService,
        private _$translate: angular.translate.ITranslateService,
        private _storeService: IStoreService,
        private _logbookService: ILogbookService,
        private _dashboardHelperService: IDashboardHelperService,
        private _dateRangeService: IDateRangeService,
        private _authorizationService: IAuthorizationService,
        private _groupTreeHelperService: IGroupTreeHelperService,
        private _exportToCsvService: IExportToCsvService,
        private _logbookTableService: ILogbookTableService
    ) {}

    $onInit() {
        this._hasManualInputRoles = this._authorizationService.isInRoles(Roles.manualInputRoles);

        if (this.item) {
            this.item.exportToCsv = this.exportToCsv.bind(this);
        }

        this.setHeadings();
        this.isLoadingVms = true;
        this._garbages.push(
            this._storeService.register((store) => this.reloadStore(store), [StoreElement.ValueViewModels, StoreElement.Groups, StoreElement.DigitalSets])
        );
    }

    $onChanges(changes: angular.IOnChangesObject) {
        if (changes['boundData']?.currentValue) {
            const currentValue = this.boundData;
            if (Array.isArray(currentValue?.entries)) {
                // We need to enter the methods below to create fake entries
                this._entries = currentValue.entries.map(toLogbookEntry);
                this.augmentedEntries = this.mapEntries();
            }
        }

        if (changes['dashboards']?.currentValue) {
            let dashboardsDic: { [guid: string]: Dashboard } = {};
            const receivedDashboards = changes['dashboards'].currentValue;
            if (receivedDashboards && Array.isArray(receivedDashboards)) {
                receivedDashboards.forEach((dashboard) => {
                    dashboardsDic[dashboard.guid] = dashboard;
                });
            } else {
                dashboardsDic = receivedDashboards;
            }
            if (receivedDashboards && receivedDashboards.length > 0) {
                this.dashboardNames = this._dashboardHelperService.getDashboardNames(receivedDashboards);
            }
            this.dashboardsDic = dashboardsDic;
        }

        if (changes['boundSeries']?.currentValue || changes['item']?.currentValue) {
            this.setHeadings();
            if (this.item.configuration.dashboardGuid === guidGroupValidationPageLink) {
                this.linkIcon = 'fa-check-square-o';
            }
        }

        if (changes['item']?.currentValue || changes['selectedGroup']?.currentValue) {
            this.preparePrefilters();
        }

        if (changes['item']?.currentValue) {
            if (this.item?.configuration) {
                this.item.configuration.timeRange = timeRangeRetroCompatibility(this.item.configuration);
            }
        }
    }

    $onDestroy() {
        this._garbages.forEach((g) => g());
    }

    gotoDestination(item: LogbookTableWidget, entry: AugmentedEntry): void {
        if (item.configuration.dashboardGuid && item.configuration.dashboardGuid !== guidEmpty) {
            const selectedGuid = this._dashboardHelperService.getSelectedGroupGuid(entry.group.guid);
            const go = this._dashboardHelperService.getGotoDestination(
                item.configuration.dashboardGuid,
                this.dashboardsDic,
                selectedGuid,
                moment(entry.range.startDate).toISOString(),
                (entry.range.endDate ? moment(entry.range.endDate) : moment()).toISOString()
            );
            this._$state.go(go.route, go.params);
        }
    }

    clickCell(value: AugmentedEntry, $event: angular.IAngularEvent): void {
        $event.stopPropagation();

        const range = {
            startDate: moment(value.range.startDate).toISOString(),
            endDate: moment(value.range.endDate).toISOString()
        };
        const entry = toLogbookEntryDto(value, false);
        const badge: BadgeVM = { severity: value.severity, entries: [entry] };
        const series = this.item.configuration?.series || [];
        const logbookSeries =
            series.length > 0 ? series.map<ILogbookModalSeries>((s) => ({ isBoundToGroup: s.isBoundToGroup, vm: this.viewModels[s.guid] })) : [];
        this._logbookService.openModal(
            value?.identifierViewModel?.guid,
            badge,
            !value?.isFake,
            this._hasManualInputRoles,
            true,
            range,
            value.severity,
            value.group,
            this.item.prefilters,
            this.item.allowedGroupGuidList,
            logbookSeries,
            this.item.configuration.taskOptionsOverride,
            this.item.configuration.launchTask
        );
    }

    hasTimeRange(timeRange: TimePeriod): boolean {
        return timeRange === TimePeriod.Both || timeRange === TimePeriod.BeginDate || timeRange === TimePeriod.EndDate;
    }

    getTimeRange(range: { startDate: string | moment.Moment; endDate: string | moment.Moment }, timeRange: TimePeriod, dateFormat: string): string {
        const inProgress = this._$translate.instant('In progress');
        return getTimeRange(range, timeRange, dateFormat, inProgress);
    }

    exportToCsv(): angular.IPromise<any> {
        const table = this._logbookTableService.buildCsvTable(this.augmentedEntries, this.item.configuration, this.headings);
        return this._exportToCsvService.getCsvFromData(table, `${this.item.name}_${moment().format('YYYY-MM-DD HHmm')}.csv`, true);
    }

    private reloadStore(store: Store) {
        const { valueViewModels, groups } = store;
        this.groups = _.values(groups.dictionary);
        const vms = valueViewModels.dictionary;
        this.viewModels = vms;
        this.digitalSets = store.digitalSetDictionary.dictionary;
        this.augmentedEntries = this.mapEntries();
        this.isLoadingVms = false;
        this.preparePrefilters();
    }

    private setHeadings() {
        const boundSerieVM = this.boundSeries
            ?.filter((d) => d.viewModel)
            .map((d, i) => {
                const unitName = getUnitName(d.viewModel.unitName, d.configuration.value);
                const vm = angular.copy(d.viewModel);
                vm.valueType = this.item.configuration.series.length === this.boundSeries.length ? this.item.configuration.series[i].value : vm.valueType;
                vm.unitName = unitName;
                return vm;
            });
        this.headings = getLogbookTableHeadings(this.item.configuration, boundSerieVM || [], this.item.map);
    }

    private mapEntries(): AugmentedEntry[] {
        const boundSeries = this.boundSeries?.map((s) => s.configuration);
        const series = boundSeries || this.item.configuration.series || [];
        const values = this.boundData?.values || [];
        series.sort((a, b) => (a.sortOrder > b.sortOrder ? 1 : -1));

        const entriesList = this.getLimitedEntries().map((e) => {
            return this.mapEntry(e, this.viewModels || {}, values, series);
        });

        return entriesList;
    }

    // #45516 limit number of displayed entries to prevent browser freeze
    // #51098 no longer limiting...
    private getLimitedEntries(): LogbookEntry[] {
        const error =
            this._entries?.length >= this.item.configuration.maxTotalItems
                ? new WideError(ErrorLevel.Warning, 'Only _number_ first results are being displayed', [
                      { number: this.item.configuration.maxTotalItems } as any
                  ])
                : null;
        this.item.setError(error);
        return this._entries ? this._entries : [];
    }

    private mapEntry(
        entry: LogbookEntry,
        viewModels: Dictionary<IValueViewModel>,
        allValues: LogbookSeriesValues[],
        series: LogbookTableSeries[]
    ): AugmentedEntry {
        const result = angular.copy(entry) as AugmentedEntry;
        if (entry.id < 0) {
            delete result.severity;
        }
        // Assign identifier if any
        if (viewModels && entry.identifier && viewModels[entry.identifier.guid]) {
            result.identifierViewModel = viewModels[entry.identifier.guid];
        }

        // Map all values to entry
        result.values = this._logbookTableService.mapEntryValues(
            entry.id,
            series,
            allValues,
            viewModels,
            this.item.configuration,
            this.digitalSets,
            this.stringValues
        );

        const displayedBegin = moment(this._dateRangeService.beginDate());
        const displayedEnd = moment(this._dateRangeService.endDate());
        const entryBegin = moment(result.range.startDate);
        const entryEnd = result.range.endDate ? moment(result.range.endDate) : undefined;

        // handle duration and in progress
        result.displayDuration = entryEnd ? moment.duration(entryEnd.diff(entryBegin)).asHours().toString() : 'In progress';

        const config = this.item.configuration;
        // handle outisde current range when including previous
        if (config.hasPreviousIncluded) {
            // as we included previous, we know enddate are in range or null
            // it may not be the cases for begin date bt depend on the read mode
            let isInRange = true;

            // handle include end date
            if (config.isEndDateIncluded) {
                displayedEnd.add(1, 'ms');
            }

            if (config.readMode === LogbookTableReadMode.ByTimeStamp) {
                const endBeforeBegin = entryEnd && entryEnd.isSameOrBefore(displayedBegin);
                const beginAfterEnd = entryBegin.isSameOrAfter(displayedEnd);
                isInRange = !(endBeforeBegin || beginAfterEnd);
            } else if (config.readMode === LogbookTableReadMode.ByModificationDate) {
                const entryModification = moment(entry.dateModified);
                isInRange = entryModification.isSameOrAfter(displayedBegin) && entryModification.isBefore(displayedEnd);
            }

            result.isOutsideRange = !isInRange;
        }

        // multiline title on hover
        result.entryTitle = this._logbookService.getMultiLineTitle([toLogbookEntryDto(entry, false)]);

        return result;
    }

    private preparePrefilters() {
        const item = this.item;
        const groups = this.groups;

        if (item && item.configuration && item.baseType === logbookTableWidgetDefinition.baseType) {
            // handle retro compatibility
            this.checkConfiguration(item.configuration);

            const filters: LogbookWidgetFilters = Object.assign({}, item.configuration.filters);
            filters.isPrefilterEntry = item.configuration.prefilterEntries;

            filters.isBoundToGroup = item.configuration.filters.isBoundToGroup;
            if (filters.isBoundToGroup) {
                filters.groupGuids = this.selectedGroup ? [this.selectedGroup.guid] : [];
                filters.groupTypes = [];
            }

            let allowedGroupGuidList = [];
            if (groups?.length > 0) {
                let filteredOnGuids = [];
                let filteredOnTypes = [];
                if (filters.groupGuids?.length > 0) {
                    filteredOnTypes = groups.filter((group) => filters.groupGuids.indexOf(group.guid) >= 0).map((group) => group.guid);
                }
                if (filters.groupTypes?.length > 0) {
                    filteredOnGuids = groups.filter((group) => filters.groupTypes.indexOf(group.type) >= 0).map((group) => group.guid);
                }
                if (filteredOnTypes.length > 0 || filteredOnGuids.length > 0) {
                    allowedGroupGuidList = _.union(filteredOnTypes, filteredOnGuids);
                }
                if (filters.includeChildren) {
                    const children = _.flatMap(allowedGroupGuidList, (guid) => this._groupTreeHelperService.getFlatChildren(groups, guid).map((g) => g.guid));
                    allowedGroupGuidList = _.union(allowedGroupGuidList, children);
                }
                this.item.allowedGroupGuidList = allowedGroupGuidList;
            }
            this.item.prefilters = filters;
        }
    }

    private checkConfiguration(config: LogbookTableConfiguration) {
        // #45739 : transform hasGroup into displayedGroupName
        if (config.hasGroup !== undefined) {
            config.groupNameDisplayed = config.hasGroup ? GroupDisplayName.UniqueName : GroupDisplayName.None;
            config.hasGroup = undefined;
        }
    }
}

export const wtLogbookTable: ComponentDefinition = {
    componentId: 'wtLogbookTable',
    controller: LogbookTableController,
    controllerAs: 'vm',
    template: require('./logbookTable.component.html'),
    bindings: {
        boundData: '<',
        boundSeries: '<?',
        dashboards: '<?',
        isEditMode: '<?',
        item: '<',
        selectedGroup: '<?',
        stringValues: '<'
    }
};